import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import useDecodeToken from "../../../../hooks/useDecodeToken";

import { getAllOrders } from "../../../../services/deliveryService";

import { orderSearchCategories } from "../../../../constants";
import {
  checkIfClient,
  API_CONSTANTS,
  retryAttempt,
  queryKeys,
  RENDER_MODE,
  addPendingReturnOrderStatus,
} from "../constants";

function useCSROrders({
  searchCategory,
  fromDate,
  toDate,
  invoices = [],
  barcodes = [],
  isClientSide = false,
  isEnabledAllOrders = true,
}) {
  const [decodedToken] = useDecodeToken();
  const history = useHistory();

  const isClient = useMemo(() => checkIfClient(decodedToken), [decodedToken]);

  const searchOrderHistoryParams = useMemo(() => {
    const baseParams = {
      searchBy: searchCategory,
      isClient,
    };

    if (searchCategory === orderSearchCategories.dateRange.value) {
      return {
        ...baseParams,
        fromDate,
        toDate,
      };
    }

    if (searchCategory === orderSearchCategories.invoice.value) {
      return {
        ...baseParams,
        selected: invoices,
      };
    }

    if (searchCategory === orderSearchCategories.barcode.value) {
      return {
        ...baseParams,
        selected: barcodes,
      };
    }
  }, [searchCategory, isClient, fromDate, toDate, invoices, barcodes]);

  const allOrdersQuery = useQuery({
    queryKey: queryKeys.ALL_ORDERS_BY(searchCategory, searchOrderHistoryParams),
    queryFn: () =>
      getAllOrders({
        ...searchOrderHistoryParams,
        timeout: API_CONSTANTS.TIMEOUT,
        tableMode: RENDER_MODE.CSR,
      }),
    retry: retryAttempt,
    select: (response) => {
      const { deliveryList, returnRequestList } = response?.data || {};

      const deliveryListWithReturnStatus = addPendingReturnOrderStatus(
        deliveryList,
        returnRequestList,
      );

      return {
        ...response?.data,
        deliveryList: deliveryListWithReturnStatus,
      };
    },
    enabled: isClientSide && isEnabledAllOrders,
    meta: { history, disabledGlobalToast: true },
    staleTime: API_CONSTANTS.STALE_TIME,
    cacheTime: API_CONSTANTS.GC_TIME,
  });

  return allOrdersQuery;
}

export { queryKeys as allOrdersQueryKeys, useCSROrders };
// export default useCSROrders;
